import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
    
    // Preload the video
    const videoElement = videoRef.current;
    
    if (videoElement) {
      // Listen for the loadeddata event to know when video is ready to play
      videoElement.addEventListener('loadeddata', () => {
        // Short timeout to ensure smooth transition
        setTimeout(() => {
          setVideoLoaded(true);
          setTimeout(() => {
            setShowPlaceholder(false);
          }, 300); // Fade out placeholder after video is visible
        }, 100);
      });
      
      // Force load the video
      videoElement.load();
    }
    
    // Preload images that will be used elsewhere on the page
    const preloadImages = [
      '/homestay/outdoor.jpg',
      // Add other important images from your site
    ];
    
    preloadImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const handleBookClick = () => {
    const element = document.getElementById('booking-form');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  };

  return (
    <div className="relative h-screen overflow-hidden">
      {/* Low-res placeholder image that shows immediately */}
      <AnimatePresence>
        {showPlaceholder && (
          <motion.div 
            className="absolute inset-0 bg-blue-900"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <img
              src="/homestay/outdoor-placeholder.jpg" 
              alt="Riverside scene placeholder"
              className="w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-black opacity-50"></div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video background with fade-in effect */}
      <motion.div 
        className="absolute inset-0"
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ 
          scale: 1, 
          opacity: videoLoaded ? 1 : 0 
        }}
        transition={{ 
          scale: { duration: 10, ease: "easeOut" },
          opacity: { duration: 0.5 }
        }}
      >
        <video
          ref={videoRef}
          src="/homestay/outdoor.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
          preload="auto"
        />
        <div className="absolute inset-0 bg-black opacity-50"></div>
      </motion.div>

      {/* Loading indicator */}
      <AnimatePresence>
        {!videoLoaded && (
          <motion.div 
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="w-16 h-16 border-t-4 border-b-4 border-yellow-400 rounded-full animate-spin"></div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 50 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            <motion.span 
              className="block"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
            >
              RIVERSIDE
            </motion.span>
            <motion.span 
              className="block text-yellow-400"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
            >
              BLISS VILLA
            </motion.span>
            <motion.span 
              className="block text-5xl md:text-7xl mt-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.1 }}
            >
              HOME STAY
            </motion.span>
          </h1>
          <motion.p 
            className="text-xl text-white mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
          >
            Experience tranquility by the river at Kallar, Kerala
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.7 }}
          >
            <motion.button
              onClick={handleBookClick}
              className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold px-6 py-3 rounded-lg text-lg transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Book Your Stay
            </motion.button>
            <motion.a
              href="tel:+919400949078"
              className="bg-white hover:bg-gray-100 text-blue-900 font-bold px-6 py-3 rounded-lg text-lg transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Call Us: 9400949078
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 2, repeat: Infinity, repeatType: "reverse" }}
      >
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
        </svg>
      </motion.div>
    </div>
  );
};

export default Hero;